import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ThumbnailName from '../ThumbnailName';
import BasicInfo from '../BasicInfo';
import CurrentEvents from '../CurrentEvents';
import MakeEvents from '../MakeEvents';
import FollowRivalList from '../FollowRivalList';
import UserEditForm from '../../forms/UserEditForm';
import PasswordEditForm from '../../forms/PasswordEditForm';
import useApiControl from '../../../common/useApiControl';

const otherDeviceSectionSx = {
  width: '100%',
  maxWidth: { xs: '360px', sm: '640px' },
  mx: 'auto',
  mt: 4,
  mb: 5,
  px: 2,
  py: 2.5,
  boxSizing: 'border-box',
  borderRadius: '12px',
  bgcolor: 'rgba(255,255,255,0.78)',
  border: '1px solid rgba(0,0,0,0.08)',
};

const logoutButtonSx = {
  mt: 2,
  width: '100%',
  maxWidth: '320px',
  color: '#fff',
  bgcolor: '#374151',
  borderColor: '#374151',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: '#1f2937',
    borderColor: '#1f2937',
    boxShadow: 'none',
  },
};

function MyUser({ user, makeEvents }) {
  const [joinEvents, setJoinEvents] = useState([]);
  const [visibleSection, setVisibleSection] = useState('makeEvents');

  // マスタータグ取得
  const { data: masterTagsData, executeRequest: fetchMasterTags } = useApiControl({
    action: '/get_master_tags',
    method: 'POST',
  });

  const joinEventsParams = useMemo(() => {
    if (user?.id) {
      return {
        userIds: [user.id],
        includesDeleted: true,
      };
    }
    return null;
  }, [user?.id]);

  const { data: joinEventsData, executeRequest: fetchJoinEvents } = useApiControl({
    action: '/get_join_events',
    params: joinEventsParams,
    method: 'POST',
  });

  useEffect(() => {
    fetchMasterTags();
  }, []);

  useEffect(() => {
    setVisibleSection('makeEvents');
  }, [user?.id]);

  useEffect(() => {
    if (!joinEventsParams) {
      setJoinEvents([]);
      return;
    }

    fetchJoinEvents().catch((error) => {
      console.error('Failed to fetch join events:', error);
    });
  }, [joinEventsParams]);

  useEffect(() => {
    setJoinEvents(joinEventsData?.events || []);
  }, [joinEventsData]);

  const handleClickDetail = (label) => {
    const sectionByLabel = {
      ルーム作成数: 'makeEvents',
      ルーム参加数: 'events',
      フォロー数: 'follow',
      ライバル数: 'rival',
    };

    const targetSection = sectionByLabel[label];
    if (!targetSection) {
      return;
    }

    setVisibleSection((prev) => (prev === targetSection ? null : targetSection));
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('event');
    localStorage.removeItem('ownersEvent');
    window.location.href = './login';
  };

  return (
    <div style={{ backgroundColor: '#fff7ef', minHeight: 'calc(100vh - 64px)' }}>
        {/* 1. サムネイル＋名前 */}
        <ThumbnailName user={user} isSelf />

        {/* 2. 基本情報 */}
        <BasicInfo
          user={user}
          makeEvents={makeEvents}
          joinedCount={joinEvents.length}
          visibleSection={visibleSection}
          onClickDetail={handleClickDetail}
        />

        {/* 3. 詳細情報（参加中イベント/フォロー/ライバル） */}
        {visibleSection === 'makeEvents' && (
          <MakeEvents makeEvents={makeEvents} masterTagsData={masterTagsData} />
        )}
        {visibleSection === 'events' && (
          <CurrentEvents joinEvents={joinEvents} masterTagsData={masterTagsData} />
        )}
        {visibleSection === 'follow' && (
          <FollowRivalList user={user} listType="follow" />
        )}
        {visibleSection === 'rival' && (
          <FollowRivalList user={user} listType="rival" />
        )}

        {/* 4. プロフィール編集 */}
        <UserEditForm user={user} masterTagsData={masterTagsData} />

        {/* 5. 別端末利用 */}
        <Box sx={otherDeviceSectionSx}>
          <Typography component="h2" sx={{ m: 0, mb: 1, fontSize: '20px', fontWeight: 800 }}>
            別の端末で遊ぶ場合
          </Typography>
          <Typography sx={{ mb: 2.5, fontSize: '13px', lineHeight: 1.7, color: 'rgba(0,0,0,0.62)' }}>
            別の端末でログインする場合は、先にパスワードを設定してください。
            この端末で遊び続ける場合、通常ログアウトは不要です。
          </Typography>
          <PasswordEditForm />
          <Box sx={{ mt: 2.5, textAlign: 'center' }}>
            <Button variant="contained" disableElevation onClick={logout} sx={logoutButtonSx}>
              ログアウト
            </Button>
          </Box>
        </Box>
      </div>
  );
}
export default MyUser;
